import React, { Component, FC, MouseEvent } from 'react';
import styled from 'styled-components';
import numbro from 'numbro';

import AppDetailsSection from '../AppDetailsSection/index';
import Layout from 'components/v2/Layout/index';
import LoadingPage from 'components/v2/LoadingPage';
import SwiperPanel from 'components/v2/SwiperPanel/index';
import { SectionTitle } from '../Common';
import { locale, isAndroid, osVersion } from 'config';
import { isValidContentString } from 'utils';
import i18n, { formatDate, getFallbackLocale, translations } from 'i18n';
import App from '../../../models/app';
import { LinkAction } from '../LinkAction/index';
import ReportModal from '../ReportModal/index';

import { IUser } from '../../../types/user';
import AppCarousel from '../AppCarousel/index';
import StateManagementButton from '../StateManagementButton/index';
import { ACTIONS } from '../StateManagementButton/types';
import { DeveloperContactButton } from '../ContactDeveloper/index';
import { ContentBlock, ContentSection } from './ContentSection';
import Labels from '../Labels/index';
import MessageCard from '../MessageCard';
import Typography, { LinkText } from '../Typography';
import PremiumSubscribeButton, { PremiumSubscribeButtonType } from '../Premium/PremiumSubscribeButton';
import ButtonBar from '../Button/ButtonBar';

import premiumLabelIcon from '../../../icons/premium-logo-text.svg';
import { AppType } from '../../../constants';
import ParentAppSection from './ParentAppSection';
import { FitbitDeeplink } from '../Deeplink';
import { getPremiumIcon } from '../../../utils.v2';

// Regular Expression for URL validation
//
// Author: Diego Perini
// Source: https://gist.github.com/dperini/729294
// Created: 2010/12/05
// Updated: 2018/09/12
// License: MIT
//
// Copyright (c) 2010-2018 Diego Perini (http://www.iport.it)
//
// Permission is hereby granted, free of charge, to any person
// obtaining a copy of this software and associated documentation
// files (the "Software"), to deal in the Software without
// restriction, including without limitation the rights to use,
// copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the
// Software is furnished to do so, subject to the following
// conditions:
//
// The above copyright notice and this permission notice shall be
// included in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES
// OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
// NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
// HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY,
// WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
// FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR
// OTHER DEALINGS IN THE SOFTWARE.
//
// Modified slightly to avoid trailing punctuation as per:
//   https://gist.github.com/dperini/729294#gistcomment-2617741
const URL_REGEX = new RegExp(
  '(' +
  // protocol identifier
  '(?:(?:https?|ftp)://)' +
  // user:pass authentication
  '(?:\\S+(?::\\S*)?@)?' +
  '(?:' +
  // IP address exclusion
  // private & local networks
  '(?!(?:10|127)(?:\\.\\d{1,3}){3})' +
  '(?!(?:169\\.254|192\\.168)(?:\\.\\d{1,3}){2})' +
  '(?!172\\.(?:1[6-9]|2\\d|3[0-1])(?:\\.\\d{1,3}){2})' +
  // IP address dotted notation octets
  // excludes loopback network 0.0.0.0
  // excludes reserved space >= 224.0.0.0
  // excludes network & broacast addresses
  // (first & last IP address of each class)
  '(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])' +
  '(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}' +
  '(?:\\.(?:[1-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))' +
  '|' +
  // host & domain names
  '(?![-_])(?:[-\\w\\u00a1-\\uffff]{0,63}[^-_]\\.)+' +
  // TLD identifier
  '(?:[a-z\\u00a1-\\uffff]{2,})' +
  ')' +
  // port number
  '(?::\\d{2,5})?' +
  // resource path, excluding a trailing punctuation mark
  '(?:[/?#](?:\\S*[^\\s!"\'()*,-.:;<>?\\[\\]_`{|}~]|))?' +
  ')',
  'gi'
);

const Page = styled.div`
  background-color: var(--sys-color--surface);
  box-sizing: border-box;
  min-height: 100%;
  width: 100%;
  padding-bottom: 16px;
`;

const MainContent = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  background: var(--sys-color--surface);
  gap: 16px;
  margin: 16px 16px 0 16px;
`;

interface ContainerProps {
  children?: React.ReactNode;
  app?: any;
}

const Container: FC<ContainerProps> = ({ children, app }) => (
  <Layout
    name="Details"
    scrollKey="details"
    center={app?.name}
    pageElement={<Page>{children}</Page>}
    app={app}
  />
);

const DescriptionContainer = styled(Typography)`
  p {
    word-break: break-word;

    &:first-of-type {
      margin-top: 0;
    }
    &:last-of-type {
      margin-bottom: 0;
    }
  }
`;

const Description = styled.div`
  height: 300px;
  overflow: hidden;
  position: relative;
`;

const ShowMoreContainer = styled.div`
  text-align: center;
  width: 100%;
`;

const Hidden = styled.div`
  background-image: linear-gradient(
    to bottom,
    transparent 0%,
    var(--sys-color--surface) 100%
  );
  height: 100px;
  position: absolute;
  bottom: 0;
  width: 100%;
`;

interface InstallButtonContainerProps {
  noMargin?: boolean;
}

const InstallButtonContainer = styled.div<InstallButtonContainerProps>`
  display: flex;
  flex-direction: row;
  gap: 16px;
  width: 100%;
  margin-bottom: ${({ noMargin }) => noMargin ? '0px' : '16px'};
  border: none !important;
  align-items: center;

  button, a, > * {
    flex: 1;
  }
`;

const PremiumLabel = styled.div`
  background: url(${premiumLabelIcon}) center center / contain no-repeat;
  width: 75px;
  height: 14px;
  margin: 15px 0px 16px;
  align-self: center;
`;

const ContactOutlineButton = styled(ButtonBar)`
  align-self: flex-start;
`;

const DeveloperContactOutlineButton: FC<any> = ({
  label,
  id,
  href,
  target,
  onClick,
}) => {
  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      onClick();
    }
    if (href) {
      window.open(href, target || '_blank');
    }
  };

  return (
    <ContactOutlineButton
      id={id}
      variant="outline"
      onClick={handleButtonClick}
    >
      {label}
    </ContactOutlineButton>
  );
};

const AFTER_DETAILS = 'AFTER_DETAILS';

interface AppDetailsProps {
  appTypeName?: string;
  app: App;
  currentUser: IUser;
  userHasGalleryGrant: boolean;
  detailsSectionTitle: string;
  screenshotsSectionTitle: string;
  phoneAppVersion: string;
  installTexts: string[];
  showRemove: boolean;
  loading: boolean;
  backText: string;
  descriptionSectionTitle: string;
  collectionId?: string | null;
}

interface AppDetailsState {
  showAll: boolean;
  showUpdateButton: boolean;
  showReportModal: boolean;
}

export default class AppDetails extends Component<AppDetailsProps, AppDetailsState> {
  static defaultProps = {
    appTypeName: 'App'
  };

  constructor(props: AppDetailsProps) {
    super(props);

    this.state = {
      showAll: false,
      showUpdateButton: true,
      showReportModal: false,
    };
  }

  navigateToSettings = (event: MouseEvent) => {
    event.preventDefault();
    const { app } = this.props;
    app.navigateToSettings();
  };

  showAll = () => {
    this.setState({ showAll: true });
  };

  onUpdateSuccess = () => {
    this.setState({ showUpdateButton: false });
  }

  renderDetails() {
    const { app, detailsSectionTitle, phoneAppVersion } = this.props;
    if (!app.latestVersion) {
      return;
    }

    const {
      component: {
        AppDetails: {
          ColumnDescriptor: {
            updated: updatedColDesc,
            size: sizeColDesc,
            version: versionColDesc,
            status: statusColDesc
          },
          privateStatusText: privateDesc,
          publicStatusText: publicDesc,
          contactDeveloper,
        }
      }
    } = translations;

    const details = [];
    if (app.updatedDate) {
      details.push({
        label: updatedColDesc(),
        value: formatDate(app.updatedDate)
      });
    }
    if (app.size) {
      details.push({
        label: sizeColDesc(),
        value: numbro(app.size).format({
          output: 'byte',
          base: 'binary',
          mantissa: 2
        })
      });
    }
    if (app.versionString) {
      details.push({
        label: versionColDesc(),
        value: app.versionString
      });
    }
    if (app.isPrivate) {
      details.push({
        label: statusColDesc(),
        value: app.isPrivate ? privateDesc() : publicDesc()
      });
    }

    return (
      <ContentSection title={detailsSectionTitle}>
        {app.type.toLowerCase() === AppType.TILE && this.renderParentAppInfo()}
        <AppDetailsSection data={details} />
        {this.renderRichDescription(AFTER_DETAILS)}
        {this.renderLearnMore()}
        <DeveloperContactButton
          id="DeveloperContactButton"
          app={app}
          phoneAppVersion={phoneAppVersion}
          deviceName={this.deviceName()}
          label={contactDeveloper()}
          LinkComponent={DeveloperContactOutlineButton}
        />
      </ContentSection>
    );
  }

  renderWarnings() {
    const { app } = this.props;

    const {
      component: {
        AppIsPaidNotice,
        AppIsPrivateNotice
      }
    } = translations;
    const warnings = [];

    if (app.isPrivate && !app.bypassPrivateAppWarning) {
      warnings.push({
        title: AppIsPrivateNotice.title(),
        text: () => (
          <React.Fragment>
            {AppIsPrivateNotice.text()}
          </React.Fragment>
        ),
        boldEndText: AppIsPrivateNotice.caution()
      })
    }

    if (app.isPaid) {
      warnings.push({
        title: AppIsPaidNotice.title(),
        text: () => (
          <React.Fragment>
            {app.type === AppType.CLOCK.toUpperCase() ? AppIsPaidNotice.textClock() : AppIsPaidNotice.textApp()}
            <b>{AppIsPaidNotice.notEndorsed()}</b>
          </React.Fragment>
        ),
        boldEndText: AppIsPaidNotice.caution()
      })
    }

    return (
      warnings.length > 0 && (
        warnings.map((warning) => (
          <MessageCard
            key={warning.title}
            category="Default"
            headline={warning.title}
            description={warning.text()}
            boldDescription={warning.boldEndText}
          />
        ))
      )
    )
  }

  deviceName() {
    const { currentUser: { device = {} } = {} } = this.props;

    return (device as any).productName || '';
  }

  openReportModal = () => {
    this.setState({ showReportModal: true });
  };

  closeReportModal = () => {
    this.setState({ showReportModal: false });
  };

  renderModal = () => {
    const { app } = this.props;

    const { showReportModal } = this.state;

    if (showReportModal) {
      return <ReportModal app={app} onClose={this.closeReportModal} />;
    }
  };

  renderRichDescription(position: string) {
    const { app } = this.props;
    if (!app.richDescription) {
      return;
    }

    if (app.richDescriptionPosition === position) {
      return <div dangerouslySetInnerHTML={{ __html: app.richDescription }} />;
    }
  }

  renderLearnMore() {
    const { app } = this.props;

    if (app && app.isInstalled && app.isOnDevice && app.learnMore && app.learnMoreDeeplink) {
      return (
        <FitbitDeeplink url={app.learnMoreDeeplink}>
          <Typography variant="labelLarge" color="var(--sys-color--on-surface-variant)">
            {app.learnMore}
          </Typography>
        </FitbitDeeplink>
      )
    }
  }

  renderDescription = () => {
    const {
      app: { description = '' }
    } = this.props;
    const { showAll } = this.state;
    const {
      component: {
        AppDetails: { showMoreButtonText }
      }
    } = translations;

    const parsedDescription = description
      .trim()
      .split('\n')
      .filter((paragraph) => isValidContentString(paragraph))
      .map((paragraph, index) => {
        const urls = paragraph.match(URL_REGEX);
        if (urls && urls.length > 0) {
          paragraph = paragraph.split(URL_REGEX).map((element, pIndex) => {
            if (urls.includes(element)) {
              return (
                <a
                  href={element}
                  key={`${index}${pIndex}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {element}
                </a>
              );
            }
            return element;
          }) as any;
        }
        return <p key={index}>{paragraph}</p>;
      });

    if (isValidContentString(description, { minLength: 530 }) && !showAll) {
      return (
        <DescriptionContainer variant="bodyLarge" color="var(--sys-color--on-surface-variant)">
          <Description>
            {parsedDescription}
            <Hidden />
          </Description>
          <ShowMoreContainer>
            <ButtonBar compact variant="text" onClick={this.showAll}>
              {showMoreButtonText()}
            </ButtonBar>
          </ShowMoreContainer>
        </DescriptionContainer>
      );
    }

    return (
      <DescriptionContainer variant="bodyLarge" color="var(--sys-color--on-surface-variant)">
        {parsedDescription}
      </DescriptionContainer>
    );
  };

  buttons(): React.ReactNode[] {
    const { app, installTexts, showRemove, collectionId } = this.props;
    const { showUpdateButton } = this.state;

    const installUninstall = (
      <StateManagementButton
        key="install-uninstall"
        app={app}
        showRemove={showRemove}
        texts={installTexts}
        action={ACTIONS.UNINSTALL}
        collectionId={collectionId}
        deviceName={this.deviceName()}
        parentApp={app.parentApp}
        isContents
      />
    );
    const update = (
      <StateManagementButton
        key="update"
        onSuccess={this.onUpdateSuccess}
        app={app}
        showRemove={showRemove}
        texts={installTexts}
        deviceName={this.deviceName()}
        parentApp={app.parentApp}
        isContents
      />
    );

    const buttons: React.ReactNode[] = [];

    const canUpdate = app.hasUpdateAvailable && !app.isClock;
    const showUninstallButton = !(app.hasUpdateAvailable && !showRemove);

    // when an update is available we can display both buttons only if the app is uninstalable
    if (showUpdateButton) {
      if (canUpdate) {
        buttons.push(update);
      }
      if (showUninstallButton) {
        buttons.push(installUninstall);
      }
    } else {
      // we should always display a button for progress
      buttons.push(installUninstall);
    }

    return buttons;
  }

  renderPremiumSubscribeButton = () => (
    <PremiumSubscribeButton
      trigger={`app-${this.props.app.id}`}
      type={PremiumSubscribeButtonType.INSTALL_BUTTON}
    />
  );

  renderImage = () => {
    const { banners = [] } = this.props.app;

    const presentationImage: any[] = [];
    if (banners[0]) {
      presentationImage.push(banners[0]);
    }

    const SwiperPanelComponent = SwiperPanel as any;

    return <SwiperPanelComponent
      banners={presentationImage}
      premiumIcon={getPremiumIcon(this.props.app, this.deviceName())}
    />;
  };

  renderReportButton = () => {
    const {
      component: {
        AppDetails: { reportButtonText }
      }
    } = translations;

    return (
      <LinkAction
        id="report-action"
        text={reportButtonText()}
        arrow={false}
        onClick={this.openReportModal}
      />
    );
  };

  renderActions = () => {
    const { app } = this.props;
    const {
      component: {
        AppDetails: { settingsLinkText, permissionLinkText }
      }
    } = translations;

    const showPermissionsLink = app.isOnDevice && !app.hidePermissions &&
      (app.hasGrantedPermissions || app.hasPermissionWarning);
    const showSettingsLink = app.isOnDevice && app.hasSettings;
    const showReportButton = app.isPrivate && !app.bypassPrivateAppWarning;

    if (!showPermissionsLink && !showSettingsLink && !showReportButton) {
      return null;
    }

    return (
      <ContentBlock px="normal" gap="none">
        {showPermissionsLink && (
          <LinkAction
            id="permissions-action"
            aria-label="permissions-action"
            arrow={false}
            text={permissionLinkText()}
            to={`/v2/${app.isClock ? 'clock' : 'app'}/${app.id}/permissions`}
            pagedirection="FORWARD"
          />
        )}
        {showSettingsLink && (
          <LinkAction
            id="settings-action"
            arrow={false}
            text={settingsLinkText()}
            onClick={this.navigateToSettings}
          />
        )}
        {showReportButton && this.renderReportButton()}
      </ContentBlock>
    );
  };

  renderSettingsButton = () => {
    const {
      component: {
        AppDetails: { customizeButtonText }
      }
    } = translations;

    return (
      <ButtonBar
        id="customize-button"
        variant="outline"
        fullWidth
        icon="edit"
        onClick={this.navigateToSettings}
      >
        {customizeButtonText()}
      </ButtonBar>
    );
  };

  computePermissionNames = (permissions: string[]) =>
    permissions.map((name: string) => {
      // for Android 10 & 11 the app requires specific Background Location access
      if (isAndroid && (osVersion === '10' || osVersion === '11') && name === 'access_location') {
        return i18n('comms.permissions.access_background_location.label')();
      }

      return i18n(`comms.permissions.${name}.label`)();
    });

  renderPermissionIssueNotice = () => {
    const {
      permissionIssueNoticeSingluar,
      permissionIssueNoticePlural,
      permissionIssueNoticeNameJoiner,
      permissionIssueNoticeLink
    } = translations.page.App.Details;

    const { app } = this.props;

    if (app.isOnDevice && app.hasPermissionWarning) {
      const permissionNames = this.computePermissionNames(app.permissionsWithIssues);

      // Link to help article to troubleshoot app issues
      const link = `https://help.fitbit.com/articles/Help_article/2275?l=${getFallbackLocale(
        locale
      ).replace('-', '_')}#troubleshoot`;

      return (
        <MessageCard
          category="Caution"
          description={
            <React.Fragment>
              {permissionNames.length > 1
                ? permissionIssueNoticePlural({
                  permissionNames: permissionNames.join(
                    ` ${permissionIssueNoticeNameJoiner()} `
                  )
                })
                : permissionIssueNoticeSingluar({
                  permissionNames: permissionNames.join(
                    ` ${permissionIssueNoticeNameJoiner()} `
                  )
                })}{' '}
              <LinkText href={link}>
                {permissionIssueNoticeLink()}
              </LinkText>
            </React.Fragment>
          }
        />
      );
    }
  };

  renderParentAppInfo = () => {
    const {
      parentAppSectionTitle
    } = translations.page.App.Details;

    const { app } = this.props;

    if (!app?.parentApp) {
      return
    }

    return (
      <ParentAppSection app={app.parentApp} />
    )
  };

  render() {
    const {
      loading,
      app,
      descriptionSectionTitle,
      phoneAppVersion,
    } = this.props;


    if (loading || !app) {
      return (
        <Container>
          <LoadingPage headerHeight={56} />
        </Container>
      );
    }

    return (
      <Container app={app}>
        <AppCarousel
          app={app}
          deviceName={app.availableForDevice ? this.deviceName() : undefined}
          appName={app.name}
          developerName={app.developer.name}
        />
        <MainContent id="mainContent">
          {this.renderWarnings()}
          {this.renderPermissionIssueNotice()}
          {this.renderModal()}
          <Labels app={app} phoneAppVersion={phoneAppVersion} deviceName={this.deviceName()} />
          <InstallButtonContainer id="installButtonContainer" noMargin={app.isPremium}>
            {app.isPremium && !this.props.userHasGalleryGrant ?
              this.renderPremiumSubscribeButton() : this.buttons()}
            {app.isClock && app.isOnDevice && app.hasSettings && this.renderSettingsButton()}
          </InstallButtonContainer>
          {app.isPremium && <PremiumLabel />}
        </MainContent>
        {isValidContentString(app.description) && (
          <ContentSection title={descriptionSectionTitle}>
            {this.renderDescription()}
          </ContentSection>
        )}
        {this.renderActions()}
        {this.renderDetails()}
      </Container>
    );
  }
}
